import React, { useContext, useEffect, useState } from 'react'
import { Button, Dimmer, Dropdown, Header, Label, Loader, Message, Popup, Segment } from 'semantic-ui-react'
import { DashboardQuery } from '~/QueryHooks/DashboardQuery'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import CustomIcon from '~/components/CustomIcon'
import { StyledEmissionWrap, StyledInformationWrapper, StyledMessage } from '../CO2Dashboard/style'
import { formatNumber, handleErrors } from '~/utils/helpers'
import MilkProductIcon from '@assets/svgs/milk-products.svg'
import Calendar from '@assets/svgs/calendar.svg'
import { DropdownValueType, Nullable } from '~/types'
import { DownloadService } from '~/api/services/downloadFiles'
import { useAuth } from '~/routes/components/AuthProvider'
import Speedometer from '~/components/Speedometer'
import { Link } from 'react-router-dom'
import { RouteTypes } from '~/routes/constants'
import MilkFatProteinIcon from '@assets/svgs/milk-fat-protein.svg'
import Cows from '@assets/images/bulls-and-cows.png'
import DataCard, { CardLabel, CardValue } from '~/components/DataCard'
import InfoPopup from '~/components/InfoPopup'
import PreviewModal from '~/pages/Farmer/Co2Climate/AnswerCO2Climate/PreviewModal'
import { ThemeContext } from 'styled-components';
import EmissionChart from './EmissionChart'

const ClimaCheckDashboard: React.FC = () => {
  const { t } = useTranslation()
  const { systemData: { logged: { farmerId } } } = useAuth()
  const { firstColor } = useContext(ThemeContext)

  const [selectedFarmerId, setSelectedFarmerId] = useState<DropdownValueType>(farmerId)

  const {
    data: co2Data,
    isLoading: isLoadingCo2Data,
    refetch: fetchCo2Data,
    isFetching: isFetchingCo2Data
  } = DashboardQuery.getDashboardClimaCheckData(selectedFarmerId)

  const {
    data: co2Options,
    isLoading: isLoadingCo2Options,
    refetch: fetchOptions
  } = DashboardQuery.getDashboardClimaCheckOptions()

  const [showData, setShowData] = useState<Nullable<{ id: string, uniqueId: string }>>(null)
  const [isLoadingEmmision, setIsLoadingEmission] = useState(false)

  useEffect(() => {
    fetchOptions()
  }, [t])

  useEffect(() => {
    if (co2Data) {
      fetchCo2Data()
    }
  }, [selectedFarmerId])

  function renderLoader() {
    return <Dimmer inverted active={isLoadingCo2Data || isLoadingCo2Options}>
      <Loader inverted active={isLoadingCo2Data || isLoadingCo2Options} />
    </Dimmer>
  }

  async function handleDownloadResults() {
    setIsLoadingEmission(true)
    try {
      await DownloadService.downloadFileWithName(`climate-check-footprint/${co2Data.lastClimateCheckFootprint.id}/get-results/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingEmission(false)
  }

  function renderLastFootprintActions() {
    const isFinishedCo2Slot = Boolean(co2Data?.avgResultFarmer)
    return <div>
      {isFinishedCo2Slot && co2Data.lastClimateCheckFootprint && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
        <Popup
          content={t('Show')}
          trigger={<CustomIcon
            size='large'
            onClick={() => setShowData({
              id: co2Data.lastClimateCheckFootprint.id,
              uniqueId: co2Data.lastClimateCheckFootprint?.uniqueId || ''
            })}
            name='eye'
          />}
        />
        <Popup
          content={<span dangerouslySetInnerHTML={{ __html: t('CO2 emission result') }} />}
          trigger={<CustomIcon
            onClick={() => handleDownloadResults()}
            loading={isLoadingEmmision}
            disabled={isLoadingEmmision}
            size='large'
            name='file pdf'
          />}
        />
      </div>}
    </div>
  }

  function renderEmissionChart() {
    const farmerNowAnsweredCo2 = !isLoadingCo2Data && !co2Data?.avgResultFarmer
    return <StyledEmissionWrap>
      <Header ribbon attached='top' block>{'THG-Bewertung'}</Header>
      <Segment style={{
        borderRadius: 5,
        position: 'relative'
      }} attached>
        <div style={{ position: 'absolute', top: 10, right: 10 }}>
          <InfoPopup
            styleIcon={{ fontSize: '1.4em' }}
            position={'top center'}
            content={'Hier sehen Sie die Zusammensetzung der Treibhausgasemissionen im Produktionssystem Milch pro Kuh und Jahr für Ihren Betrieb mit der prozentualen Aufteilung nach den Emissionsquellen.'}
          />
        </div>
        {farmerNowAnsweredCo2 ?
          <div>{'Keine Ergebnisse von Datenerhebungen vorahnden.'}</div> :
          <EmissionChart avgResultsFarmer={co2Data?.avgResultFarmer} />}
      </Segment>
    </StyledEmissionWrap>
  }

  function renderDataCollections() {
    return <>
      <DataCard
        title={t('Period under consideration')}
        renderActions={() => renderLastFootprintActions()}
        value={co2Data?.lastClimateCheckFootprint?.periodFrom ?
          `${dayjs(co2Data?.lastClimateCheckFootprint?.periodFrom).format('DD.MM.YYYY')} - ${dayjs(co2Data?.lastClimateCheckFootprint?.periodTo).format('DD.MM.YYYY')}` :
          t('Not defined')}
        icon={<img style={{ width: '2em', marginRight: 3 }} src={Calendar} />}
        content={<>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: '0.5em' }}>
            <CardLabel>{t('Status')}:</CardLabel>
            <CardValue>{co2Data?.lastClimateCheckFootprint?.statusLabel}</CardValue>
          </div>
        </>
        }
      />
      <DataCard
        title={t('Milk sold')}
        value={!co2Data?.climateCheckData?.totalMilkAmount ? '0 kg' : `${formatNumber(co2Data?.climateCheckData?.totalMilkAmount || 0, 0)} kg`}
        icon={<img style={{ width: '1.5em', marginRight: 5 }} src={MilkProductIcon} />}
      />
      <DataCard
        title={'Fettgehalt / Eiweißgehalt'}
        icon={<img style={{ width: '1.5em', marginRight: 5 }} src={MilkFatProteinIcon} />}
        value={co2Data?.climateCheckData?.fat ? `${formatNumber(co2Data?.climateCheckData?.fat || 0, 2)}% / ${formatNumber(co2Data?.climateCheckData?.protein || 0, 2)}%` : t('Not defined')}
      />
      <DataCard
        title={t('⌀ inventory')}
        icon={<img style={{ width: '2em', marginRight: 5 }} src={Cows} />}
        value={!co2Data?.climateCheckData?.cowsNumber ? '0 Milchkühe' : `${formatNumber(co2Data?.climateCheckData?.cowsNumber || 0, 0)} Milchkühe`}
      />
    </>
  }

  function renderLastCo2Data() {
    const farmerNowAnsweredCo2 = !isLoadingCo2Data && !co2Data?.avgResultFarmer
    return <div style={{ display: 'flex', alignItems: 'baseline', gap: 20, justifyContent: 'space-between' }}>
      <Speedometer
        co2Data={co2Data}
        min={0}
        footprintColor={co2Options?.climateCheckFootprintSettings?.lflValueColor}
        co2FootprintSettings={co2Options?.climateCheckFootprintSettings}
        max={Math.max(...[Number(co2Data?.avgResultFarmer?.value) || 0, Number(co2Data?.defaultResult) || 0, Number(co2Data?.avgDairyResults) || 0]) + 0.2}
        legend
        dairyLabel={co2Options?.texts ? co2Options?.texts[0]?.translation : ''}
      />
      {renderEmissionChart()}
      <StyledInformationWrapper>
        <Header ribbon attached='top' block>{t('Data collection')}</Header>
        <Segment style={{
          position: 'relative',
          borderRadius: 5
        }} attached>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            height: '100%',
            justifyContent: 'space-between'
          }}>
            {farmerNowAnsweredCo2 ?
              <div
                style={{ marginTop: '50%', textAlign: 'center' }}>{'Keine Ergebnisse von Datenerhebungen vorahnden.'}
              </div>
              :
              renderDataCollections()}
          </div>
        </Segment>
      </StyledInformationWrapper>
    </div>
  }

  function renderShowModal() {
    return <PreviewModal
      id={showData?.id}
      handleClose={() => {
        setShowData(null)
      }}
      uniqueId={showData?.uniqueId}
      farmerId={farmerId || ''}
    />
  }

  function renderFarmerSupplier() {
    const hasDefaultResults = !isLoadingCo2Data && !co2Data?.defaultResult
    const displayDefaultResults = Boolean(co2Options.climateCheckFootprintSettings.showLflDefaultValue)
    const displayNoDefaultResultsMessage = hasDefaultResults && displayDefaultResults

    if (co2Options.farmers?.length > 1) {
      return <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', gap: 20 }}>
        {displayNoDefaultResultsMessage && <StyledMessage warning>
          <Message.Content>{'Die Daten von der LfL API konnten nicht geladen werden, sodaass Sie nur ihre Referenzwerte und die Ihrer Molkerei angezeigt bekommen.'}</Message.Content>
        </StyledMessage>}
        <Dropdown
          selection
          selectOnBlur={false}
          style={{ marginBottom: '2em', maxWidth: 300 }}
          value={selectedFarmerId}
          options={co2Options.farmers.map(farmer => ({ value: farmer.farmerId, text: `${farmer.companyName} (${farmer.supplierNo})` }))}
          onChange={(_, data) => setSelectedFarmerId(data.value)}
          placeholder={t('Select supplier number')}
          disabled={isFetchingCo2Data}
          loading={isFetchingCo2Data}
        />
      </div>
    }

    if (co2Options.farmers?.length === 1) {
      return <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', gap: 20 }}>
        {displayNoDefaultResultsMessage && <StyledMessage warning>
          <Message.Content>{'Die Daten von der LfL API konnten nicht geladen werden, sodaass Sie nur ihre Referenzwerte und die Ihrer Molkerei angezeigt bekommen.'}</Message.Content>
        </StyledMessage>}
        <Label
          style={{
            marginLeft: 'auto',
            marginBottom: '2em',
            color: 'white',
            background: firstColor,
            maxWidth: 300
          }}
        >{`${co2Options.farmers[0].companyName} (${co2Options.farmers[0].supplierNo})`}</Label>
      </div>
    }

    return displayNoDefaultResultsMessage && <StyledMessage warning>
      <Message.Content>{'Die Daten von der LfL API konnten nicht geladen werden, sodaass Sie nur ihre Referenzwerte und die Ihrer Molkerei angezeigt bekommen.'}</Message.Content>
    </StyledMessage>
  }

  return <>
    {renderLoader()}
    <div style={{ marginTop: '2em', display: 'flex', flexDirection: 'column', minHeight: '60vh', justifyContent: 'space-between' }}>
      <div>
        {!isLoadingCo2Options && renderFarmerSupplier()}
        {!isLoadingCo2Options && renderLastCo2Data()}
      </div>
      <Button as={Link} to={RouteTypes.REPORTING_CLIMA_CHECK} basic style={{ marginTop: '2em' }}>{t('View complete report')}</Button>
    </div>
    {renderShowModal()}
  </>
}
export default ClimaCheckDashboard